<script setup lang="ts">
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'

// Rows that lead to subpages, like the iOS settings: an icon tile, a title with a hint line below it, an optional value and a
// chevron, in rounded cards of related rows. The card is a `UiList` inside a `UiGroup`, the rows are `UiRow`s.
export interface NavItem {
  to: string
  icon: string
  title: string
  hint?: string
  value?: string
}

export interface NavGroup {
  key: string
  items: NavItem[]
  title?: string
  footer?: string
}

defineProps<{ groups: NavGroup[] }>()
</script>

<template>
  <nav>
    <UiGroup
      v-for="group in groups"
      :key="group.key"
      :title="group.title"
      :footer="group.footer"
      data-testid="nav-group"
    >
      <UiList>
        <UiRow v-for="item in group.items" :key="item.to" :to="item.to" class="nav-row">
          <span class="tile" aria-hidden="true">{{ item.icon }}</span>
          <span class="text">
            <span class="name">{{ item.title }}</span>
            <span v-if="item.hint" class="ui-hint">{{ item.hint }}</span>
          </span>
          <span v-if="item.value" class="value" aria-hidden="true">{{ item.value }}</span>
          <span class="go" aria-hidden="true">›</span>
        </UiRow>
      </UiList>
    </UiGroup>
  </nav>
</template>

<style scoped>
.nav-row {
  color: var(--tg-text-color);
  text-decoration: none;
}

.tile {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
  background: var(--tg-secondary-bg-color);
  font-size: 1.1rem;
  line-height: 1;
}

.text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.name {
  overflow-wrap: anywhere;
}

.ui-hint {
  font-size: 0.78rem;
}

.value {
  flex: none;
  font-size: 1.1rem;
  line-height: 1;
}

.go {
  flex: none;
  color: var(--tg-hint-color);
  font-size: 1.2rem;
}
</style>
